import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography
} from '@mui/material';
import { CityMunicipality, Precinct } from '../../types';
import React, { useState } from 'react';
import { collection, deleteDoc, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { db, functions } from '../../firebase';

import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MergeIcon from '@mui/icons-material/Merge';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ReplicateIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import UpdateIcon from '@mui/icons-material/Update';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const Utilities: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const tabulateExitPolls = async () => {
    if (!user) return;

    try {
      const tabulateExitPollsFunc = httpsCallable(functions, 'tabulate_exit_polls', { timeout: 1000 * 60 * 5 });
      const result = await tabulateExitPollsFunc({
        dashboardId: 'SURIGAO DEL NORTE_DISTRICT 1'
      });
      console.log('Exit polls tabulated:', result);
    } catch (error) {
      console.error('Error tabulating exit polls:', error);
    }
  };

  const tabulateStubReleases = async () => {
    if (!user) return;

    try {
      const tabulateStubReleasesFunc = httpsCallable(functions, 'tabulate_stub_releases', { timeout: 1000 * 60 * 5 });
      const result = await tabulateStubReleasesFunc({
        dashboardId: 'SURIGAO DEL NORTE_DISTRICT 1'
      });
      console.log('Stub releases tabulated:', result);
    } catch (error) {
      console.error('Error tabulating stub releases:', error);
    }
  };

  const tabulateFinalReleases = async () => {
    if (!user) return;

    try {
      const tabulateFinalReleasesFunc = httpsCallable(functions, 'tabulate_final_releases', { timeout: 1000 * 60 * 5 });
      const result = await tabulateFinalReleasesFunc({
        dashboardId: 'SURIGAO DEL NORTE_DISTRICT 1'
      });
      console.log('Final releases tabulated:', result);
    } catch (error) {
      console.error('Error tabulating final releases:', error);
    }
  };

  const refreshTabulations = async () => {
    if (!user) return;

    try {
      const refreshTabulationsFunc = httpsCallable(functions, 'refresh_tabulations', { timeout: 1000 * 60 * 5 });
      const result = await refreshTabulationsFunc({
        dashboardId: 'SURIGAO DEL NORTE_DISTRICT 1'
      });
      console.log('Tabulations refreshed:', result);

      setSnackbarMessage('Tabulations refresh triggered');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error tabulations refresh:', error);
      setSnackbarMessage('Failed to refresh tabulations');
      setSnackbarOpen(true);
    }
  };

  const clearDomoyog = async () => {
    const domoyogPrecinctsPath = '/tabulationsV2/SURIGAO DEL NORTE_DISTRICT 1/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts/DISTRICT 1/cities/DEL CARMEN/barangays/DOMOYOG/precincts';
    const domoyogPrecinctsColRef = collection(db, domoyogPrecinctsPath);
    const precinctDocsSnap = await getDocs(domoyogPrecinctsColRef);
    for (const precinctDoc of precinctDocsSnap.docs) {
      const precinctVotersColRef = collection(precinctDoc.ref, "voters");
      const precinctVotersDocsSnap = await getDocs(precinctVotersColRef);
      for (const voterPollDoc of precinctVotersDocsSnap.docs) {
        console.log(voterPollDoc.id);
        await deleteDoc(voterPollDoc.ref);
      }
    }
    console.log('DOMOYOG cleared');
  };

  const buildCitiesList = async () => {
    if (!user) return;

    try {
      const precinctsColRef = collection(db, "precincts");
      const q = query(precinctsColRef,
        where("province", "==", "SURIGAO DEL NORTE")
      );
      const precinctDocsSnap = await getDocs(q);

      // create a set containing all cities and municipalities
      const cities: CityMunicipality[] = [];
      for (const doc of precinctDocsSnap.docs) {
        const precinct = doc.data() as Precinct;
        if (precinct.city_municipality) {
          // check if the city_municipality is already in the set
          const existingCity = cities.find((c) => c.city_municipality === precinct.city_municipality && c.province === precinct.province);
          if (existingCity) {
            continue;
          }
          cities.push({
            id: `${precinct.city_municipality}_${precinct.province}`,
            city_municipality: precinct.city_municipality,
            district: precinct.district,
            province: precinct.province
          });
        }
      }

      const citiesColRef = collection(db, "cities_municipalities");
      for (const city of cities) {
        console.log(`Adding city: ${city.city_municipality} (${city.province})`);
        const cityRef = doc(citiesColRef, city.id);
        await setDoc(cityRef, city);
      }
    } catch (error) {
      console.error("Error fetching barangays:", error);
    }
  };

  const utilityOptions = [
    {
      label: 'Voter CSV Import',
      icon: <ImportExportIcon />,
      path: '/utilities/voters/import/csv'
    },
    {
      label: 'Del Carmen Voters CSV Import',
      icon: <ImportExportIcon />,
      path: '/utilities/del-carmen/import/csv'
    },
    {
      label: 'Dapa Poll Additional CSV Import',
      icon: <ImportExportIcon />,
      path: '/utilities/dapa/additional-import/csv'
    },
    {
      label: 'Dapa Poll CSV Import',
      icon: <ImportExportIcon />,
      path: '/utilities/dapa/import/csv'
    },
    {
      label: 'Dapa Poll Import Errors',
      icon: <ErrorOutlineIcon />,
      path: '/utilities/dapa/import-errors'
    },
    {
      label: 'Socorro Poll CSV Import',
      icon: <ImportExportIcon />,
      path: '/utilities/socorro/import/csv'
    },
    {
      label: 'Socorro Poll Import Errors',
      icon: <ErrorOutlineIcon />,
      path: '/utilities/socorro/import-errors'
    },
    {
      label: 'Pilar Batch Poll',
      icon: <ImportExportIcon />,
      path: '/utilities/pilar-batch-poll'
    },
    {
      label: 'Refresh Voter Keywords',
      icon: <SearchIcon />,
      path: '/utilities/voters/keywords-index/refresh'
    },
    {
      label: 'Refresh Tabulations',
      icon: <DashboardIcon />,
      onClick: refreshTabulations
    },
    {
      label: 'Resolve Precinct Voters',
      icon: <ManageAccountsIcon />,
      path: '/utilities/precinct/resolve-voters'
    },
    {
      label: 'Update Voter Count',
      icon: <UpdateIcon />,
      path: '/utilities/update-voter-count'
    },
    {
      label: 'Remove Tabulation Mismatch',
      icon: <RemoveCircleIcon />,
      path: '/utilities/remove-tab-mismatch'
    },
    {
      label: 'Replicate Vote',
      icon: <ReplicateIcon />,
      path: '/utilities/replicate-vote'
    },
    {
      label: 'Tabulate Exit Polls',
      icon: <ExitToAppIcon />,
      onClick: tabulateExitPolls
    },
    {
      label: 'Tabulate Stub Releases',
      icon: <LocalActivityIcon />,
      onClick: tabulateStubReleases
    },
    {
      label: 'Tabulate Final Releases',
      icon: <ConfirmationNumberIcon />,
      onClick: tabulateFinalReleases
    },
    {
      label: 'Build Dapa Dashboard',
      icon: <ExitToAppIcon />,
      path: '/utilities/build_dapa_dashboard'
    },
    {
      label: 'Upmerge Poll Responses',
      icon: <MergeIcon />,
      path: '/utilities/upmerge_poll_responses'
    },
    {
      label: 'Clear Domoyog',
      icon: <DeleteForeverIcon />,
      onClick: clearDomoyog
    },
    {
      label: 'Purge Voters',
      icon: <DeleteForeverIcon />,
      path: '/utilities/purge-voters'
    }
  ];

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Utilities
      </Typography>
      <List>
        {utilityOptions.map((option, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton 
              onClick={option.path ? 
                () => navigate(option.path) : 
                option.onClick
              }
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText primary={option.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Utilities;
